import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { skillCategories } from '../../data/skills';
import { members } from '../../data/members';
import SectionTitle from '../common/SectionTitle';
import TechIcon from '../ui/TechIcon';
import '../../styles/components/Skills.css';

const getMemberName = (id: string, lang: string): string => {
  const member = members.find(m => m.id === id);
  if (!member) return id;
  return lang === 'vi' && member.nameVi ? member.nameVi : member.name;
};

/* Category icon mapping — decorative accent per category */
const categoryIcons: Record<string, string> = {
  Languages: '</>', Frameworks: '{ }', 'CMS/Builders': '[]',
  'Cloud & DevOps': '>>>', 'Design Tools': '~', Databases: ';;', Other: '...',
};

/* Stagger container */
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

/* Card entrance */
const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

/* Skill pill entrance */
const pillVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const Skills: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <section id="skills" className="section skills">
      <div className="container">
        <SectionTitle title={t('skills.title')} subtitle={t('skills.subtitle')} />

        <motion.div
          className="skills__grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {skillCategories.map((category) => {
            const catName = language === 'vi' ? category.nameVi : category.name;
            const catIcon = categoryIcons[category.name] || '...';

            return (
              <motion.div
                key={category.name}
                className="skills__card"
                variants={cardVariants}
              >
                {/* Card header */}
                <div className="skills__card-header">
                  <span className="skills__card-icon" aria-hidden="true">{catIcon}</span>
                  <h3 className="skills__card-title">{catName}</h3>
                  <span className="skills__card-count">{category.skills.length}</span>
                </div>

                {/* Skills list */}
                <motion.div
                  className="skills__pills"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {category.skills.map((skill) => (
                    <motion.div
                      key={skill.name}
                      className="skills__pill"
                      variants={pillVariants}
                      whileHover={{ y: -3, scale: 1.04 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <span className="skills__pill-icon" aria-hidden="true">
                        <TechIcon name={skill.name} />
                      </span>
                      <span className="skills__pill-name">{skill.name}</span>

                      {/* Tooltip */}
                      <div className="skills__tooltip" role="tooltip">
                        <span className="skills__tooltip-label">{t('skills.usedBy')}:</span>
                        {skill.members.map(id => (
                          <span key={id} className="skills__tooltip-member">
                            {getMemberName(id, language)}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
